import { useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import { MAX_SEARCH_QUERY_BYTES, SEARCH_PAGE_SIZE } from '@/protocol/limits';
import { WorkerCallError, type SearchHitResult } from '@/transport/workerCalls';
import type { WorkerClient } from '@/transport/workerClient';
import type { Hex } from '@/store/ids';
import type { MemberRecord } from '@/store/types';
import styles from '@/components/panels.module.css';

export interface SearchPanelProps {
  worker: WorkerClient | null;
  conversationId: Hex;
  members: readonly MemberRecord[];
  onOpenResult: (messageId: string) => void;
  onClose: () => void;
}

type Status = 'idle' | 'loading' | 'done' | 'unavailable' | 'offline' | 'failed';

/**
 * Search within one conversation.
 *
 * Section 06 ships search inside a single dialog first, restricted to explicit
 * authorized conversations, with phrase/word search and media-type, sender and
 * date filters, at most 50 results, and no arbitrary user regular expressions.
 * The query is sent as typed and is never compiled into a pattern here.
 *
 * `SEARCH_UNAVAILABLE` is presented distinctly from being offline. Section 06
 * makes it the one retryable error of its group and requires send and history
 * to keep working through it, so the message says exactly that instead of
 * implying the connection is gone.
 */
export function SearchPanel(props: SearchPanelProps) {
  const { t, formatRelativeDay } = useStrings();
  const [query, setQuery] = useState('');
  const [senderId, setSenderId] = useState('');
  const [mediaKind, setMediaKind] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [hits, setHits] = useState<SearchHitResult[]>([]);

  const tooLong = new TextEncoder().encode(query).length > MAX_SEARCH_QUERY_BYTES;

  const run = async () => {
    const worker = props.worker;
    if (!worker || query.trim().length === 0 || tooLong) {
      return;
    }
    setStatus('loading');
    try {
      const result = await worker.call('search-history', {
        conversationIds: [props.conversationId],
        query: query.trim(),
        senderId: senderId === '' ? null : (senderId as Hex),
        mediaKind: mediaKind === '' ? null : mediaKind,
        from: null,
        to: null,
        cursor: null,
        limit: SEARCH_PAGE_SIZE,
      });
      setHits(result.hits);
      setStatus('done');
    } catch (error) {
      const code = error instanceof WorkerCallError ? error.code : 'TRANSPORT';
      setStatus(
        code === 'SEARCH_UNAVAILABLE' ? 'unavailable' : code === 'OFFLINE' ? 'offline' : 'failed',
      );
      setHits([]);
    }
  };

  return (
    <section className={styles.panel} aria-label={t('search.inConversation')}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>{t('search.inConversation')}</h2>
        <button type="button" className={styles.action} onClick={props.onClose}>
          {t('search.close')}
        </button>
      </div>

      <div className={styles.panelBody}>
        <div className={styles.field}>
          <label htmlFor="conversation-search">{t('search.placeholder')}</label>
          <input
            id="conversation-search"
            className={styles.input}
            type="search"
            dir="auto"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault();
                void run();
              }
            }}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="search-sender">{t('search.filterSender')}</label>
          <select
            id="search-sender"
            className={styles.select}
            value={senderId}
            onChange={(event) => {
              setSenderId(event.target.value);
            }}
          >
            <option value="">{t('search.filterAnySender')}</option>
            {props.members.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.displayName || member.userId.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="search-media">{t('search.filterMedia')}</label>
          <select
            id="search-media"
            className={styles.select}
            value={mediaKind}
            onChange={(event) => {
              setMediaKind(event.target.value);
            }}
          >
            <option value="">{t('search.filterAnyMedia')}</option>
            <option value="photo">{t('search.mediaPhoto')}</option>
            <option value="video">{t('search.mediaVideo')}</option>
            <option value="file">{t('search.mediaFile')}</option>
            <option value="voice">{t('search.mediaVoice')}</option>
          </select>
        </div>

        <div className={styles.buttonRow}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={status === 'loading' || query.trim().length === 0 || tooLong}
            onClick={() => {
              void run();
            }}
          >
            {status === 'loading' ? t('search.loading') : t('search.run')}
          </button>
        </div>

        {tooLong ? (
          <p className={styles.error} role="alert">
            {t('search.tooLong')}
          </p>
        ) : null}
        {status === 'unavailable' ? (
          <p className={styles.error} role="alert">
            {t('search.unavailable')}
          </p>
        ) : null}
        {status === 'offline' || status === 'failed' ? (
          <p className={styles.error} role="alert">
            {t('search.offline')}
          </p>
        ) : null}

        {status === 'done' && hits.length === 0 ? (
          <p className={styles.empty}>{t('search.empty')}</p>
        ) : null}

        {hits.length > 0 ? (
          <ul className={styles.list} aria-label={t('search.results')}>
            {hits.map((hit) => (
              <li key={hit.messageId}>
                <button
                  type="button"
                  className={styles.row}
                  style={{ width: '100%' }}
                  aria-label={t('search.openResult')}
                  onClick={() => {
                    props.onOpenResult(hit.messageId);
                  }}
                >
                  <span className={styles.rowBody}>
                    {/*
                      Rendered as a text node. The server returns highlight
                      ranges rather than markup, and this client never turns a
                      server string into HTML.
                    */}
                    <span className={styles.rowTitle} dir="auto">
                      {hit.text}
                    </span>
                    <span className={styles.rowMeta}>{formatRelativeDay(hit.createdAt)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
